import NotFoundPage from '@/app/ErrorHandling/NotFoundPage';
import { PageTitle } from '@/components/PageTitle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FetchDeviceQuery,
  FetchSubDevicesQuery,
  UpdateCameraActivationZoneMut,
  UpdateSecondaryDeviceMut,
} from '@/lib/api/devices/devices';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyContainer } from '@/components/platform/CopyContainer';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { usePolygonEditor } from '@/lib/geometry/usePolygonEditor';
import { DeviceTooolbar } from '../components/DeviceToolbar';
import {
  Form,
  FormControl,
  FormDescription,
  FormDirtyIndicator,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CameraFormProvider, useCameraFormCtx } from './CameraFormProvider';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { DeviceCapabilitiesBadge } from '@/components/device/DeviceCapabilitiesBadge';
import { Checkbox } from '@/components/ui/checkbox';
import { Capabilities, SecondaryDeviceFullDto } from '@/lib/api/types.gen';
import { Label } from '@/components/ui/label';
import { useWatch } from 'react-hook-form';
import { Activity, Cctv } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getVideoProcessorId } from '@/app/Devices/utils/utils';
import { useAuthStore } from '@/store/authStore';
import { EmbeddedIngestionView } from '@/features/System/ingestion/components/EmbeddedIngestionView';

export const CameraPageInner = (props: { deviceId: string; cameraId: string }) => {
  const form = useCameraFormCtx();
  const isSuperAdmin = useAuthStore((s) => s.roles.includes('SuperAdmin'));
  const ingestionEnabled = useFeatureFlagEnabled(FeatureFlags.IngestionViewer);
  const queryClient = useQueryClient();
  const { deviceId, cameraId } = props;
  const deviceQuery = useQuery(FetchDeviceQuery({ deviceId }));
  const subDevicesQuery = useQuery(FetchSubDevicesQuery({ deviceId }));
  const device = deviceQuery.data;
  const subDevices = subDevicesQuery.data ?? [];
  const camera = subDevices.find((c) => c.id === cameraId);
  const { customerId, projectId } = device ?? {};
  const updateCameraMut = useMutation({
    ...UpdateSecondaryDeviceMut(deviceId),
    onError({ message }) {
      toast({ title: 'Failed to update camera settings', description: message });
    },
    onSuccess() {
      toast({ title: 'Camera settings updated successfully' });
      queryClient.invalidateQueries({
        queryKey: FetchSubDevicesQuery({ deviceId }).queryKey,
      });
    },
  });
  const updateCameraActivationZoneMut = useMutation({
    ...UpdateCameraActivationZoneMut(cameraId),
    onError({ message }) {
      toast({ title: 'Error while updating camera grids.', description: message });
    },
  });
  const [hasLoadedImg, setHasLoadedImg] = useState(false);
  const [ingestionOpen, setIngestionOpen] = useState(false);
  // const eventsQuery = useQuery({
  //   ...SearchEventsQuery({ companyId: customerId ?? '', projectId: projectId ?? '' }),
  //   enabled: !!customerId && !!projectId,
  // });

  const poly = usePolygonEditor({ readonly: !hasLoadedImg });

  const polygons = useWatch({ control: poly.form.control, name: 'polygons' });

  // const streamQuery = useQuery(
  //   FetchFullStreamQuery({
  //     occurred: new Date(),
  //     projectId: device?.projectId ?? '',
  //     videoProcessorIdentifier: camera?.videoProcessorId?.value ?? '',
  //     companyId: device?.customerId ?? '',
  //   })
  // );

  const isLoading = deviceQuery.isLoading || subDevicesQuery.isLoading;

  useEffect(() => {
    const features = poly.util.fromWkt(camera?.activationWkt ?? '');

    poly.form.reset({ polygons: features });
  }, [poly.util.fromWkt, poly.form, camera?.activationWkt]);

  const isDirty = poly.form.formState.isDirty || form.formState.isDirty;
  const isSubmitting = updateCameraMut.isPending || updateCameraActivationZoneMut.isPending;

  const handleSubmit = form.handleSubmit((data) => {
    console.debug('Updating camera', data);

    // Update grids
    if (poly.form.formState.isDirty) {
      updateCameraActivationZoneMut.mutate({
        wkt: poly.util.toWkt(poly.form.getValues('polygons')),
      });
    }

    const capabilities: Capabilities = {
      ...data.capabilities,
    };
    const { dirtyFields } = form.formState;

    if (dirtyFields.capabilities?.canTalkdown) {
      capabilities.canTalkdown = data.capabilities.canTalkdown;
    }

    const devices = subDevices.map((device) => {
      return device.id === cameraId
        ? {
            id: camera?.id,
            description: data.description,
            monitored: data.monitored,
            quillNotes: data.quillNotes,
            capabilitiesOverride: capabilities,
            capabilities: capabilities,
          }
        : device;
    });

    updateCameraMut.mutate({
      primaryDeviceId: deviceId,
      devices: devices,
    });
  });

  const handleReset = () => {
    poly.form.reset();
    form.reset();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-y-2">
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (camera?.id == null || device == null) {
    return <NotFoundPage />;
  }
  
  return (
    <>
      <PageTitle title={camera.name || 'Camera'} />
      <DeviceTooolbar
        deviceId={deviceId}
        cameraId={cameraId}
        cameraName={camera.name ?? undefined}
        deviceName={device.name}
        customerId={customerId}
        projectId={projectId}
        siteId={device.siteId}
      >
        <div className="flex flex-1 items-center justify-end gap-x-2 p-3 text-white">
          {isSuperAdmin && ingestionEnabled && camera?.datalayerVpc && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIngestionOpen(true)}
            >
              <Activity className="mr-1.5 h-3.5 w-3.5" />
              Ingestion
            </Button>
          )}
          <Button variant="outline" disabled={!isDirty || isSubmitting} onClick={handleReset}>
            Reset
          </Button>
          <Button disabled={!isDirty || isSubmitting} type="submit" form="camera-form">
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </DeviceTooolbar>

      {isSuperAdmin && ingestionEnabled && camera?.datalayerVpc && (
        <Dialog open={ingestionOpen} onOpenChange={setIngestionOpen}>
          <DialogContent className="h-[90vh] max-w-[90vw] flex flex-col gap-0 p-0 bg-[#111] border-[#2E3835]">
            <DialogHeader className="shrink-0 border-b border-[#2E3835] px-4 py-3">
              <DialogTitle className="text-sm font-medium text-white">
                Ingestion — {camera.name || 'Camera'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto">
              <EmbeddedIngestionView lockedFilters={{ videoProcessorConfigId: camera.datalayerVpc }} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Form {...form}>
        <form id="camera-form" onSubmit={handleSubmit}>
            <div className="flex w-full flex-row gap-y-4">
              <Tabs className="flex-1" defaultValue="grid">
                {/* <TabsList className="w-full">
                <TabsTrigger value="feeds">Video Feeds</TabsTrigger>
                  <TabsTrigger value="grid">Edit Grid</TabsTrigger>
                </TabsList> */}

                <TabsContent value="feeds"></TabsContent>

                <TabsContent value="grid">
                  <div
                    className="relative h-max w-full"
                    ref={poly.targetRef}
                    data-onboarding="camera-grid-editor"
                  >
                    <DeviceSnapshot
                      deviceId={camera.id}
                      projectId={device.projectId}
                      companyId={device.customerId}
                      videoProcessorId={getVideoProcessorId(camera)}
                      className="pointer-events-none w-full select-none"
                      onLoad={() => {
                        poly.reload();
                        setHasLoadedImg(true);
                      }}
                    />
                    <canvas ref={poly.canvasRef} {...poly.canvasProps} />
                  </div>
                  <div className="flex items-start justify-end px-4 text-sm">
                    <div className="flex items-center gap-x-2">
                      <span className="text-white/70">
                        {polygons.length === 0
                          ? 'Entire Frame Motion Detection'
                          : 'Motion Detection Zone'}
                      </span>
                      {polygons.length > 0 && (
                        <div className="h-3 w-3 rounded border border-lime bg-lime/30" />
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div
                className="flex h-screen w-[380px] flex-col justify-between gap-y-4 overflow-scroll border-l  bg-[#111]  shadow-md 2xl:w-[440px] "
                style={{ maxHeight: 'calc(100vh - 112px)' }}
              >
                <div className="pb-16">
                       <div className="border-b p-4">
                    <CopyContainer
                      value={[camera.vendor, camera.model].filter(Boolean).join(' ')}
                      label="Vendor and Model"
                    >
                      <div className="flex gap-x-2  text-sm opacity-70">
                        <Cctv className="mr-2 h-6 w-6" />
                        <div className="flex flex-col items-start">
                        <p className="font-bold">{camera.vendor?.trim()}</p>
                        <p className="font-medium">{camera.model?.trim()}</p></div>
                      </div>
                    </CopyContainer>
                  </div>
                  <FormField
                    control={form.control}
                    name="monitored"
                    render={({ field }) => {
                      return (
                        <div
                          data-onboarding="camera-monitoring-toggle"
                          className={cn(
                            ' flex gap-x-4 border m-2  bg-green-darker/40 px-4 py-4 rounded-lg',
                            { 'bg-gray-800': !field.value }
                          )}
                        >
                          <Switch
                            className="mt-2"
                            checked={field.value}
                            onBlur={field.onBlur}
                            onCheckedChange={field.onChange}
                            id="monitoring"
                          />
                          <label className="space-y-2" htmlFor="monitoring">
                            <span className="flex items-center">
                              <p className="font-bold">
                                Monitoring is {field.value ? 'Enabled' : 'Disabled'}{' '}
                              </p>
                              <FormDirtyIndicator />
                            </span>
                            <p className="text-xs opacity-80">
                              When disabled, camera events will be suppressed and alerts will not be
                              surfaced to Quill nor monitoring agents.
                            </p>
                          </label>
                        </div>
                      );
                    }}
                  />


                  {/* Form with description and quill notes */}
                  <div className="mt-4 space-y-4 px-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Name <FormDirtyIndicator />
                          </FormLabel>
                          <FormControl>
                            <Input disabled placeholder="Enter name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div
                      data-onboarding="camera-capabilities"
                      className="space-y-1 border rounded-lg bg-green-darker/40"
                    >
                      <Label className="p-2 py-3 block">Capabilities</Label>

                      <div className="grid grid-cols-1">
                        {(['canLivestream', 'canPlayback', 'canTalkdown'] as const).map(
                          (capability) => {
                            const isEnabled = capability === 'canTalkdown';

                            return (
                              <FormField
                                control={form.control}
                                name={`capabilities.${capability}`}
                                key={capability}
                                render={({ field }) => {
                                  return (
                                    <div className="flex w-full items-center gap-x-4 border-t px-3 py-2 hover:bg-muted/10">
                                      <Checkbox
                                        disabled={!isEnabled}
                                        id={capability}
                                        checked={field.value ?? false}
                                        onCheckedChange={(checked) => {
                                          field.onChange(checked);
                                        }}
                                      />
                                      <label
                                        htmlFor={capability}
                                        className={cn(
                                          'flex-1 select-none py-0.5 font-medium',
                                          !isEnabled
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'cursor-pointer'
                                        )}
                                      >
                                        <DeviceCapabilitiesBadge
                                          type={capability}
                                          className="text-sm"
                                        />
                                      </label>
                                      <FormDirtyIndicator />
                                    </div>
                                  );
                                }}
                              />
                            );
                          }
                        )}
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem data-onboarding="camera-description">
                          <FormLabel>
                            Description <FormDirtyIndicator />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="Enter description" {...field} />
                          </FormControl>
                          <FormDescription>Add additional details about this device</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="quillNotes"
                      render={({ field }) => (
                        <FormItem data-onboarding="camera-quill-notes">
                          <FormLabel>
                            Additional Quill Instructions
                            <FormDirtyIndicator />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="Enter quill notes" {...field} />
                          </FormControl>
                          <FormDescription>
                            Provide extra context or instructions for Quill
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Form>
    </>
  );
};

export const CameraPage = () => {
  const params = useParams();
  const deviceId = String(params.deviceId);
  const cameraId = String(params.cameraId);

  return (
    <CameraFormProvider deviceId={deviceId} cameraId={cameraId}>
      <CameraPageInner deviceId={deviceId} cameraId={cameraId} />
    </CameraFormProvider>
  );
};
